import React, { useState, useEffect, useRef } from 'react';
import {
  uploadFileShare, getPhoneDirectory,
} from '../services/fileShareService';
import {
  FiUpload, FiSend, FiCheck, FiFile, FiSearch, FiUsers, FiX, FiShare2,
} from 'react-icons/fi';

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const FileShareQuickCard = ({ token, currentUser, kpiCardBgColor, kpiCardTextColor, onOpenFullWidget }) => {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const [expanded, setExpanded] = useState(false);
  const fileInputRef = useRef(null);

  // Load users on mount
  useEffect(() => {
    if (!token) return;
    setUsersLoading(true);
    getPhoneDirectory(token)
      .then(data => setUsers(data.filter(u => u.userId !== currentUser?.id)))
      .catch(err => console.error('Could not load users:', err))
      .finally(() => setUsersLoading(false));
  }, [token, currentUser]);

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setMsg('');
    if (!expanded) setExpanded(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFileSelect(file);
  };

  const toggleUser = (userId) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleSend = async () => {
    if (!selectedFile) { setMsg('Velg en fil først'); return; }
    if (selectedUserIds.size === 0) { setMsg('Velg minst én mottaker'); return; }
    setUploading(true);
    setMsg('');
    try {
      await uploadFileShare(token, selectedFile, Array.from(selectedUserIds));
      setMsg(`Sendt til ${selectedUserIds.size} mottaker(e)!`);
      setSelectedFile(null);
      setSelectedUserIds(new Set());
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setMsg(err.message || 'Kunne ikke sende fil');
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setSelectedFile(null);
    setSelectedUserIds(new Set());
    setMsg('');
    setExpanded(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const filteredUsers = users.filter(u => {
    const name = `${u.firstName} ${u.lastName}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  const msgIsError = msg.includes('Kunne') || msg.includes('Velg');
  const accent = '#8b5cf6';
  const iconBg = '#ede9fe';
  const iconColor = '#7c3aed';

  return (
    <div
      className="tw-group tw-relative tw-flex tw-flex-col tw-p-3 tw-rounded-xl tw-border tw-border-slate-200/80 tw-shadow-sm tw-w-full tw-text-left tw-transition-all tw-duration-200 tw-overflow-visible"
      style={{ backgroundColor: kpiCardBgColor || '#ffffff', minWidth: expanded ? '280px' : 'auto', maxWidth: '420px' }}
    >
      <span className="tw-absolute tw-top-0 tw-left-0 tw-h-full tw-w-1 tw-rounded-l-xl" style={{ background: accent }} />

      {/* Header row — KPI card style */}
      <div className="tw-flex tw-items-center tw-gap-2.5">
        <div
          className="tw-w-7 tw-h-7 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-transition-transform group-hover:tw-scale-110"
          style={{ background: iconBg, color: iconColor }}
        >
          <FiShare2 size={13} strokeWidth={2} />
        </div>
        <div className="tw-flex-1 tw-min-w-0">
          <div className="tw-text-[10px] tw-font-semibold tw-text-slate-500 tw-mt-0 tw-uppercase tw-tracking-wide" style={{ color: kpiCardTextColor || undefined }}>
            Fildeling
          </div>
          <div className="tw-text-xs tw-font-bold tw-leading-tight tw-truncate" style={{ color: kpiCardTextColor || undefined }}>
            {selectedFile ? selectedFile.name : 'Dra fil her for å dele'}
          </div>
        </div>
        {selectedFile && (
          <button
            onClick={(e) => { e.stopPropagation(); reset(); }}
            className="tw-flex-shrink-0 tw-w-5 tw-h-5 tw-rounded tw-flex tw-items-center tw-justify-center tw-text-slate-400 hover:tw-text-slate-600 hover:tw-bg-slate-100 tw-transition-colors"
            title="Fjern"
          >
            <FiX size={12} />
          </button>
        )}
      </div>

      {/* Drop zone — always visible as a thin bar, expands when file selected */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`tw-mt-2 tw-rounded-lg tw-border-2 tw-border-dashed tw-cursor-pointer tw-transition-all tw-duration-200 tw-flex tw-items-center tw-justify-center tw-gap-1.5 ${dragOver ? 'tw-border-violet-400 tw-bg-violet-50' : 'tw-border-slate-200 hover:tw-border-slate-300 tw-bg-slate-50/50'}`}
        style={{ padding: selectedFile ? '0.5rem' : '0.75rem' }}
      >
        {selectedFile ? (
          <div className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-min-w-0">
            <FiFile size={16} className="tw-text-violet-500 tw-flex-shrink-0" />
            <div className="tw-flex-1 tw-min-w-0">
              <div className="tw-text-[11px] tw-font-semibold tw-truncate" style={{ color: kpiCardTextColor || '#1e293b' }}>
                {formatBytes(selectedFile.size)}
              </div>
            </div>
            <span className="tw-text-[10px] tw-text-slate-400 tw-flex-shrink-0">Bytt fil</span>
          </div>
        ) : (
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <FiUpload size={14} className="tw-text-slate-400" />
            <span className="tw-text-[11px] tw-font-medium tw-text-slate-400">Dra og slipp eller klikk for å velge fil</span>
          </div>
        )}
        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileInputChange} />
      </div>

      {/* Expanded section — recipients + send */}
      {selectedFile && (
        <div className="tw-mt-2 tw-space-y-2">
          {/* Recipient search */}
          <div className="tw-relative">
            <FiSearch size={12} className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400" />
            <input
              type="text"
              placeholder="Søk mottaker..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="tw-w-full tw-py-1.5 tw-pl-7 tw-pr-3 tw-text-[11px] tw-border tw-border-slate-200 tw-rounded-lg tw-outline-none focus:tw-border-violet-400 tw-transition-colors"
              style={{ color: kpiCardTextColor || '#1e293b' }}
            />
          </div>

          {/* User list */}
          <div className="tw-border tw-border-slate-200 tw-rounded-lg tw-max-h-32 tw-overflow-y-auto tw-bg-slate-50/50">
            {usersLoading ? (
              <div className="tw-p-2 tw-text-center tw-text-[11px] tw-text-slate-400">Laster...</div>
            ) : filteredUsers.length === 0 ? (
              <div className="tw-p-2 tw-text-center tw-text-[11px] tw-text-slate-400">Ingen brukere</div>
            ) : (
              filteredUsers.map(u => {
                const selected = selectedUserIds.has(u.userId);
                const fullName = `${u.firstName} ${u.lastName}`;
                return (
                  <div
                    key={u.userId}
                    onClick={(e) => { e.stopPropagation(); toggleUser(u.userId); }}
                    className={`tw-flex tw-items-center tw-gap-1.5 tw-px-2 tw-py-1 tw-cursor-pointer tw-border-b tw-border-slate-100 last:tw-border-b-0 tw-transition-colors ${selected ? 'tw-bg-violet-50' : 'hover:tw-bg-slate-100'}`}
                  >
                    <div
                      className="tw-w-3.5 tw-h-3.5 tw-rounded tw-flex tw-items-center tw-justify-center tw-flex-shrink-0"
                      style={{
                        border: selected ? 'none' : '1.5px solid #cbd5e1',
                        backgroundColor: selected ? '#7c3aed' : 'transparent',
                      }}
                    >
                      {selected && <FiCheck size={9} color="white" />}
                    </div>
                    <span className={`tw-text-[11px] tw-truncate ${selected ? 'tw-font-semibold' : ''}`} style={{ color: kpiCardTextColor || '#1e293b' }}>
                      {fullName}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* Selected count + send button */}
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-slate-500 tw-flex-shrink-0">
              <FiUsers size={11} />
              {selectedUserIds.size}
            </div>
            <button
              onClick={handleSend}
              disabled={uploading || !selectedFile || selectedUserIds.size === 0}
              className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-py-1.5 tw-rounded-lg tw-text-[11px] tw-font-bold tw-border-0 tw-transition-all"
              style={{
                backgroundColor: uploading || !selectedFile || selectedUserIds.size === 0 ? '#cbd5e1' : '#7c3aed',
                color: 'white',
                cursor: uploading ? 'wait' : 'pointer',
              }}
            >
              <FiSend size={12} />
              {uploading ? 'Sender...' : 'Send fil'}
            </button>
          </div>
        </div>
      )}

      {/* Message */}
      {msg && (
        <div
          className="tw-mt-1.5 tw-px-2 tw-py-1 tw-rounded-md tw-text-[10px] tw-font-medium"
          style={{
            backgroundColor: msgIsError ? '#fee2e2' : '#d1fae5',
            color: msgIsError ? '#dc2626' : '#059669',
          }}
        >
          {msg}
        </div>
      )}

      {/* Footer link to full widget */}
      {onOpenFullWidget && !selectedFile && (
        <div
          onClick={(e) => { e.stopPropagation(); onOpenFullWidget(); }}
          className="tw-mt-1.5 tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-slate-400 hover:tw-text-violet-500 tw-transition-colors tw-cursor-pointer"
        >
          <FiShare2 size={10} />
          Mottatte / sendte filer
        </div>
      )}
    </div>
  );
};

export default FileShareQuickCard;
